"use client"

import { usePageTransition } from "@/app/(marketing)/_components/page-transition"
import { toolByName } from "@/app/(marketing)/_data/tools"
import { useCases } from "@/app/(marketing)/use-cases/_data/use-cases"
import { t, useLanguage } from "@/app/(marketing)/use-cases/_lib/i18n"
import { Button } from "@sophy-ai/ui/button"
import Image from "next/image"
import Link from "next/link"
import { notFound, useParams } from "next/navigation"
import { useCallback } from "react"

export default function UseCaseDetailPage() {
  const params = useParams<{ slug: string }>()
  const language = useLanguage()
  const { navigateWithFade } = usePageTransition()

  const handleNavigate = useCallback(
    (e: React.MouseEvent, href: string) => {
      e.preventDefault()
      navigateWithFade(href)
    },
    [navigateWithFade]
  )

  const useCase = useCases.find((uc) => uc.slug === params.slug)

  if (!useCase) {
    notFound()
  }

  const related = useCases
    .filter((uc) => uc.slug !== useCase.slug && uc.tools.some((tool) => useCase.tools.includes(tool)))
    .slice(0, 3)

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <section className="sticky top-[54px] z-[90] bg-white border-b border-gray-100 mt-[54px]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link
              href="/use-cases"
              onClick={(e) => handleNavigate(e, "/use-cases")}
              className="font-medium hover:underline"
              style={{ color: "#26251f" }}
            >
              {t(language, "活用例", "Use cases", "使用案例")}
            </Link>
            <span style={{ color: "#ccc" }}>/</span>
            <span className="truncate" style={{ color: "#666" }}>
              {t(language, useCase.title.ja, useCase.title.en, useCase.title.zh)}
            </span>
          </nav>
        </div>
      </section>

      {/* Hero */}
      <section className="pt-10 pb-12 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight mb-4" style={{ color: "#26251f" }}>
            {t(language, useCase.title.ja, useCase.title.en, useCase.title.zh)}
          </h1>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: "#666" }}>
            {t(language, useCase.description.ja, useCase.description.en, useCase.description.zh)}
          </p>

          {useCase.tools.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {useCase.tools.map((tool) => {
                const info = toolByName[tool]
                return (
                  <span
                    key={tool}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-50 border border-gray-200 text-sm"
                    style={{ color: "#26251f" }}
                  >
                    {info && (
                      <Image
                        src={info.logo}
                        alt={info.name}
                        width={16}
                        height={16}
                        className="w-4 h-4 object-contain"
                      />
                    )}
                    {tool}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Steps */}
      <section className="px-4 md:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-0">
            {useCase.steps.map((step, i) => (
              <div key={i} className="flex gap-5 md:gap-8">
                <div className="flex flex-col items-center">
                  <div
                    className="w-9 h-9 rounded-lg border border-gray-200 bg-white flex items-center justify-center flex-shrink-0 text-sm font-semibold"
                    style={{ color: "#26251f" }}
                  >
                    {i + 1}
                  </div>
                  {i < useCase.steps.length - 1 && <div className="w-px flex-1 bg-gray-200 my-2" />}
                </div>
                <div className="pb-10">
                  <h3 className="text-lg md:text-xl font-bold mb-3" style={{ color: "#26251f" }}>
                    {t(language, step.title.ja, step.title.en, step.title.zh)}
                  </h3>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: "#444" }}>
                    {t(language, step.description.ja, step.description.en, step.description.zh)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-6 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <a href="/api/auth/sign-up">
            <Button size="lg" className="bg-black hover:bg-gray-800 text-white px-8 h-12 text-base">
              {t(language, "無料で始める", "Get Started Free", "免费开始")}
            </Button>
          </a>
        </div>
      </section>

      {/* Related Use Cases */}
      {related.length > 0 && (
        <section className="px-4 md:px-6 py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-bold mb-8 text-center" style={{ color: "#26251f" }}>
              {t(language, "関連する活用例", "Related Use Cases", "相关使用案例")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((uc) => (
                <Link
                  key={uc.slug}
                  href={`/use-cases/${uc.slug}`}
                  onClick={(e) => handleNavigate(e, `/use-cases/${uc.slug}`)}
                  className="group rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg hover:border-gray-300 transition-all duration-200"
                >
                  <h3 className="text-base font-semibold mb-2" style={{ color: "#26251f" }}>
                    {t(language, uc.title.ja, uc.title.en, uc.title.zh)}
                  </h3>
                  <p className="text-sm leading-relaxed line-clamp-2" style={{ color: "#666" }}>
                    {t(language, uc.description.ja, uc.description.en, uc.description.zh)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
